/**
 * Which safety controls are ON right now (CIO, 2026-09-17).
 *
 * Every control in this system fails CLOSED: a guard that cannot satisfy itself stops the thing
 * it guards. That is the right direction and it is invisible — on 2026-09-17 the ladder-group
 * config check misread a strike out of IBKR's label, decided the frozen config disagreed with
 * the book, and switched grouping OFF in production. Nothing on screen was wrong; the feature
 * was simply inert, and only a post-deploy read of the live payload noticed.
 *
 * So the harvest card carries this line. The states come from the SERVER
 * (HelionBackend/strategy/control_status.py); nothing is judged here, because a second opinion
 * about whether a control is on is one more thing that can disagree.
 *
 * ABSENCE IS NOT "ALL ON": a payload without the block reads as unknown and renders nothing,
 * never a green light.
 */

/** States (strategy/control_status.py STATE_*). */
export type ControlState = "on" | "off" | "problem" | "unknown";

export type ControlRow = { key: string; label: string; state: ControlState; detail: string };

export type ControlStatus = {
  controls: ControlRow[];
  allOn: boolean;
  problemCount: number;
  offCount: number;
  summary: string;
};

const STATES: readonly ControlState[] = ["on", "off", "problem", "unknown"];

/** Read `controlStatus` from the home_run_harvest payload. Null when absent or malformed. */
export function readControlStatus(homeRun: unknown): ControlStatus | null {
  const block = (homeRun as { controlStatus?: unknown } | null | undefined)?.controlStatus as
    | Record<string, unknown>
    | null
    | undefined;
  if (!block || typeof block !== "object" || !Array.isArray(block.controls)) return null;
  const controls = (block.controls as Record<string, unknown>[])
    .filter((c) => c && typeof c === "object" && typeof c.key === "string")
    .map((c) => ({
      key: String(c.key),
      label: String(c.label ?? c.key),
      state: (STATES.includes(c.state as ControlState) ? c.state : "unknown") as ControlState,
      detail: typeof c.detail === "string" ? c.detail : "",
    }));
  if (!controls.length) return null;
  const problemCount = controls.filter((c) => c.state === "problem" || c.state === "unknown").length;
  const offCount = controls.filter((c) => c.state === "off").length;
  return {
    controls,
    // Recomputed from the rows rather than trusted: a stale `allOn` beside a problem row is the
    // one combination that must not render as a green light.
    allOn: problemCount === 0 && offCount === 0,
    problemCount,
    offCount,
    summary: typeof block.summary === "string" && block.summary ? block.summary : "",
  };
}

/** Chip tone per state, in the cockpit vocabulary (components/cockpit ChipTone). */
export function controlTone(state: ControlState): "positive" | "warning" | "danger" | "neutral" {
  if (state === "on") return "positive";
  if (state === "problem") return "danger";
  if (state === "unknown") return "warning";
  return "neutral";
}

/** The one-line headline: what to say when nothing needs attention, and what to say when it does. */
export function describeControls(status: ControlStatus | null): { tone: "positive" | "warning" | "danger" | "neutral"; text: string } {
  if (!status) {
    return { tone: "warning", text: "Control status unavailable — this card cannot say which controls are on." };
  }
  const problems = status.controls.filter((c) => c.state === "problem");
  const unknown = status.controls.filter((c) => c.state === "unknown");
  const off = status.controls.filter((c) => c.state === "off");
  if (problems.length) {
    return {
      tone: "danger",
      text: `${problems.map((c) => c.label).join(", ")} ${problems.length === 1 ? "needs" : "need"} attention — a control is on but cannot do its job.`,
    };
  }
  if (unknown.length) {
    return { tone: "warning", text: `${unknown.map((c) => c.label).join(", ")} could not be established.` };
  }
  if (off.length) {
    return { tone: "neutral", text: `${off.map((c) => c.label).join(", ")} switched off by configuration.` };
  }
  return { tone: "positive", text: "All controls on." };
}
